import { Ionicons } from '@expo/vector-icons';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard, Platform, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';

import { useSearchNpoAISearch, useSearchNpoByName } from '@/api/hooks/nonProfitOrgHooks';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import InputAutocompleteField from './InputAutocompleteField';
import { PredictionType } from './TextIputAutocomplete';
import AISearchButton from '../buttons/AISearchButton';

interface SearchNpoAutoCompleteProps {
  variant?: 'default' | 'rounded_with_shadow';
  leadingIcon?: React.ReactNode;
  traillingIcon?: React.ReactNode;
  onCloseSuggestions?: () => void;
  claimed?: boolean;
  canReceiveDonations?: boolean;
  placeholder?: string;
  showSearch?: boolean;
  predictionsScrollEnabled?: boolean;
  shouldClearValue?: boolean;
  onSearch?: () => void;
  onNoResults?: () => void;
  onPredictionTap: (prediction: PredictionType) => void;
}

const SearchNpoAutoComplete = ({
  variant = 'default',
  leadingIcon,
  traillingIcon,
  onCloseSuggestions,
  claimed,
  canReceiveDonations,
  predictionsScrollEnabled = true,
  shouldClearValue = false,
  placeholder = i18n.t('home.search_placeholder'),
  showSearch = false,
  onSearch,
  onNoResults,
  onPredictionTap
}: SearchNpoAutoCompleteProps) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    setValue: setSearchValue
  } = useForm<{ searchTerm: string }>();
  const { width } = useWindowDimensions();
  const { mutateAsync: searchNpoByName, isPending } = useSearchNpoByName();
  const { mutateAsync: searchNpoAI, isPending: isPendingAISearch } = useSearchNpoAISearch();

  const [search, setSearch] = useState({ term: '', fetchPredictions: false });
  const [showPredictions, setShowPredictions] = useState(false);
  const [predictions, setPredictions] = useState<PredictionType[]>([]);
  const [closeSuggestions, setCloseSuggestion] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Only for web, on mobile using react native ellipsizeMode="middle"
  const formatPredictionAddress = useCallback(
    (address?: string) => {
      if (!address) return undefined;
      // lg breakpoint --> 1024 px
      if (Platform.OS !== 'web' || address.length < 12 || width >= 1024) return address;
      const parts = address.split(',');

      if (parts.length > 1) {
        const firstPart = parts[0].trim();
        const secondPart = parts[1].trim();
        const rest = parts.slice(2).join(',').trim();

        return `${firstPart.length > 12 ? `${firstPart.slice(0, 12)}…` : firstPart}, ${secondPart.length > 10 ? `${secondPart.slice(0, 10)}...` : secondPart} ${rest}`;
      }

      return address;
    },
    [width]
  );

  const onChangeText = useCallback(async () => {
    if (search.term === undefined) return;
    if (search.term.trim() === '') {
      setShowPredictions(false);
      setPredictions([]);
      return;
    }

    if (search.fetchPredictions) {
      setHasSearched(true);
    }

    if (!search.fetchPredictions) return;

    searchNpoByName(
      { name: search.term, claimed: claimed, canReceiveDonations: canReceiveDonations, limit: 20 },
      {
        onSuccess: (result) => {
          let predictionsData: any[] = [];

          if (Array.isArray(result)) {
            predictionsData = result;
          } else if (
            result &&
            typeof result === 'object' &&
            'results' in result &&
            Array.isArray((result as any).results)
          ) {
            predictionsData = (result as any).results;
          }

          if (predictionsData.length === 0) {
            setPredictions([]);
            setShowPredictions(true);
            return;
          }

          const predictions = predictionsData
            .filter(
              (
                sugesstion
              ): sugesstion is {
                id: string;
                name: string;
                address?: string;
              } => sugesstion != null && typeof sugesstion === 'object' && 'id' in sugesstion && 'name' in sugesstion
            )
            .map((sugesstion) => ({
              value: sugesstion.id,
              label: sugesstion.name,
              extraLabel: formatPredictionAddress(sugesstion.address)
            }));

          if (predictions.length > 0) {
            setPredictions(predictions);
            setShowPredictions(true);
          } else {
            setPredictions(predictions);
            setShowPredictions(true);
          }
        },
        onError: (e) => {
          console.error(e);
        }
      }
    );
  }, [search.term, search.fetchPredictions, searchNpoByName, claimed, canReceiveDonations, formatPredictionAddress]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useMemo(
    () =>
      debounce(() => {
        if (search.term === undefined) return;
        if (search.term.trim() === '') {
          setShowPredictions(false);
          setPredictions([]);
          return;
        }
        if (!search.fetchPredictions) return;
      }, 400),
    [search.term]
  );

  useEffect(() => {
    debounced();
    return () => {
      debounced.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.term]);

  useEffect(() => {
    if (shouldClearValue) {
      setSearch({ term: '', fetchPredictions: false });
      setShowPredictions(false);
      setPredictions([]);
      setHasSearched(false);
    }
  }, [shouldClearValue]);

  const onPredictionTapped = useCallback(
    async ({ value, label }: PredictionType) => {
      setSearchValue('searchTerm', value);
      setSearch({ term: label, fetchPredictions: false });
      setShowPredictions(false);
      onPredictionTap({ value: value, label: label });
    },
    [setSearchValue, onPredictionTap]
  );

  const onSearchSubmitted: SubmitHandler<{ searchTerm: string }> = useCallback(
    async (_) => {
      Keyboard.dismiss();
      if (search.term.trim() === '') {
        setShowPredictions(false);
        setPredictions([]);
        onNoResults && onNoResults();
        onSearch && onSearch();
        return;
      }

      setHasSearched(true);

      searchNpoByName(
        { name: search.term, claimed: claimed },
        {
          onSuccess: (result) => {
            let predictionsData: any[] = [];

            if (Array.isArray(result)) {
              predictionsData = result;
            } else if (
              result &&
              typeof result === 'object' &&
              'results' in result &&
              Array.isArray((result as any).results)
            ) {
              predictionsData = (result as any).results;
            }

            if (predictionsData.length === 0) {
              setPredictions([]);
              setShowPredictions(true);
              return;
            }

            const predictions = predictionsData
              .filter(
                (sugesstion): sugesstion is { id: string; name: string; address?: string } =>
                  sugesstion != null && typeof sugesstion === 'object' && 'id' in sugesstion && 'name' in sugesstion
              )
              .map((suggestion: { id: string; name: string; address?: string }) => {
                return {
                  value: suggestion.id,
                  label: suggestion.name,
                  extraLabel: formatPredictionAddress(suggestion.address)
                };
              });

            if (predictions.length > 0) {
              setPredictions(predictions);
              setShowPredictions(true);
            } else {
              onNoResults && onNoResults();
            }
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
      onSearch && onSearch();
    },
    [search.term, claimed, searchNpoByName, onNoResults, onSearch]
  );

  const onSearchAISubmitted: SubmitHandler<{ searchTerm: string }> = useCallback(
    async (_) => {
      Keyboard.dismiss();
      if (search.term === undefined) return;
      if (search.term.trim() === '') {
        setShowPredictions(false);
        setPredictions([]);
        return;
      }

      if (search.fetchPredictions) {
        setHasSearched(true);
      }

      if (!search.fetchPredictions) return;

      searchNpoAI(
        { query: search.term },
        {
          onSuccess: (result) => {
            let predictionsData: any[] = [];

            if (Array.isArray(result)) {
              predictionsData = result;
            } else if (
              result &&
              typeof result === 'object' &&
              'results' in result &&
              Array.isArray((result as any).results)
            ) {
              predictionsData = (result as any).results;
            }

            if (predictionsData.length === 0) {
              setPredictions([]);
              setShowPredictions(true);
              return;
            }

            const predictions = predictionsData
              .filter(
                (
                  sugesstion
                ): sugesstion is {
                  id: string;
                  name: string;
                  address?: string;
                } => sugesstion != null && typeof sugesstion === 'object' && 'id' in sugesstion && 'name' in sugesstion
              )
              .map((sugesstion) => ({
                value: sugesstion.id,
                label: sugesstion.name,
                extraLabel: formatPredictionAddress(sugesstion.address)
              }));

            if (predictions.length > 0) {
              setPredictions(predictions);
              setShowPredictions(true);
            } else {
              setPredictions(predictions);
              setShowPredictions(true);
            }
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    },
    [search.term, search.fetchPredictions, searchNpoAI, formatPredictionAddress]
  );

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);
  const handleCloseSuggestions = useCallback(() => {
    Keyboard.dismiss();
    setSearch({ term: '', fetchPredictions: false });
    setShowPredictions(false);
    setPredictions([]);
    if (onCloseSuggestions) setCloseSuggestion(true);
  }, [onCloseSuggestions]);
  const handleTextChange = useCallback((text: string) => {
    setSearch({ term: text, fetchPredictions: true });
  }, []);

  return (
    <>
      <InputAutocompleteField
        variant={variant}
        leadingIcon={leadingIcon}
        traillingIcon={
          traillingIcon ? (
            traillingIcon
          ) : (
            <TouchableOpacity onPress={handleCloseSuggestions}>
              <Ionicons size={24} name="close" style={styles.iconColor} />
            </TouchableOpacity>
          )
        }
        closeSuggestions={closeSuggestions}
        onCloseSuggestions={onCloseSuggestions}
        onChangeText={handleTextChange}
        loading={isPending}
        predictions={predictions}
        control={searchControl}
        name={'searchTerm'}
        placeholder={placeholder}
        showPredictions={showPredictions}
        onPredictionTapped={onPredictionTapped}
        value={search.term}
        showNoResult={true}
        predictionsScrollEnabled={predictionsScrollEnabled}
        onSubmitEditing={({ nativeEvent }) => {
          Keyboard.dismiss();
          setSearch({ term: nativeEvent.text, fetchPredictions: true });
          onChangeText();
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        submitBehavior="submit"
        extraPredictionComponent={
          hasSearched && (Platform.OS === 'web' || isFocused) ? (
            <AISearchButton
              onPress={handleSearchSubmit(onSearchAISubmitted)}
              containerStyle={styles.aiSearchButton}
              gradientStyle={styles.aiSearchGradient}
              isLoading={isPendingAISearch}
            />
          ) : undefined
        }
        extraPredictionItemHeight={
          predictions.length === 1 ? styles.aiSearchButton.height - 13 : styles.aiSearchButton.height
        }
        extraPredictionFullWidth={true}
      />
      {showSearch && (
        <BrandedButton style={styles.searchButton} onPress={handleSearchSubmit(onSearchSubmitted)}>
          {i18n.t('mix.search')}
        </BrandedButton>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  searchButton: {
    marginTop: 15,
    marginBottom: 25
  },

  iconColor: {
    color: '#808080'
  },

  aiSearchButton: {
    height: 68,
    marginVertical: 0,
    borderRadius: 0
  },

  aiSearchGradient: {
    height: 68,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12
  }
});

export default SearchNpoAutoComplete;
